
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchBar } from './ui';
import { useHeaderData } from '@/hooks/useHeaderData';

const Header: React.FC = () => {
  const { dropdownRef, isLoggedIn, isDropdownOpen, userBilling, getUserInitials, logout, setIsDropdownOpen } = useHeaderData();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`w-full bg-brand-light-gray border-b border-gray-100 sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'py-2 shadow-md' : 'py-3'}`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo + Preview Tag */}
          <div className="flex items-center space-x-3">
            <Link to="/" className="no-underline hover:opacity-80 transition-opacity" data-testid="logo-link">
              {/* Logo Image - will be replaced when asset is provided */}
              <div className="flex items-center">
                {/* TODO: Replace with actual logo image */}
                <img
              src="/assets/logos/promptshelf-logo.svg"
              alt="PromptShelf.AI"
              className={`w-auto transition-all duration-300 ${isScrolled ? 'h-16' : 'h-24'}`}
                  onError={(e) => {
                    // Fallback to text if image not found
                    e.currentTarget.style.display = 'none';
                    const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                    if (nextElement) {
                      nextElement.style.display = 'block';
                    }
                  }}
                />
                <div className="text-xl font-bold text-brand-dark-blue hidden">
                  PromptShelf.AI
                </div>
              </div>
            </Link>
            {/* Preview Access Tag */}
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm">
              Preview Access
            </span>
          </div>
          
          {/* Search bar */}
          <SearchBar />
          
          <nav className="flex items-center space-x-8">
            <Link
              to="/teams"
              className="text-brand-dark-blue hover:text-brand-electric-blue text-sm font-medium hidden lg:block transition-colors"
            >
              Pre-Order Teams Access
            </Link>
            {isLoggedIn ? (
              <div className="flex items-center space-x-4">
                {/* User Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-8 h-8 bg-brand-electric-blue text-white rounded-full flex items-center justify-center text-sm font-medium hover:bg-brand-dark-blue transition-colors shadow-sm"
                    aria-label="User menu"
                  >
                    {getUserInitials()}
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-3 text-sm text-brand-dark-blue hover:bg-brand-light-gray transition-colors"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Settings
                      </Link>
                      {userBilling?.planName !== 'Free'
                        ? (
                          <Link
                            to="/billing"
                            className="flex items-center px-4 py-3 text-sm text-brand-dark-blue hover:bg-brand-light-gray transition-colors"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Billing
                          </Link>
                        ) : (
                          <Link
                            to="/billing?view=subscription"
                            className="flex items-center px-4 py-3 text-sm text-brand-electric-orange hover:bg-brand-sand-beige transition-colors font-medium"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            🚀 Upgrade
                          </Link>
                        )
                      }
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          logout();
                        }}
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-brand-dark-blue hover:text-brand-electric-blue text-sm font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-brand-electric-blue text-white px-4 py-2 rounded text-sm font-medium hover:bg-brand-dark-blue transition-colors shadow-sm"
                >
                  Sign Up
                </Link>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export { Header };

// Export memoized version for better performance
export default React.memo(Header);
